import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  ExperienceTranslation,
  ExperienceTranslationV2,
  ExperienceCommercialContent,
  ExperienceOperationalTranslation,
  Flow,
  ExperienceMedia,
  ExperienceOptionName,
} from "@/types/generated/ContentCommandApi";
import { AvailableLanguage } from "@/types/Language";
import { GenericHighlight, CustomHighlightsEndpoint } from "@/types/Highlights";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { createHttpInstance } from "@/features/core-shared/utils/http/http";
import { emit } from "@rsql/emitter";
import builder from "@rsql/builder";

interface ContentCommandConfig extends AxiosRequestConfig {
  params: {
    language_code: AvailableLanguage;
  };
}

type ContentCommandApi = {
  _axiosInstance: AxiosInstance;
  getTranslation: <T extends ExperienceTranslation>(
    url: `experience-translations/${string}`,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  getTranslations: <T extends ExperienceTranslation[]>(
    url: "experience-translations",
    config?: ContentCommandConfig
  ) => Promise<AxiosResponse<T>>;

  putTranslation: <T extends ExperienceTranslationV2>(
    url: `experience-translations/${string}`,
    data: T
  ) => Promise<AxiosResponse>;

  postTranslation: <T extends ExperienceTranslation>(url: "experience-translations", data: T) => Promise<AxiosResponse>;

  putExperienceMedia: <T extends ExperienceMedia>(url: `experience-media/${string}`, data: T) => Promise<AxiosResponse>;

  postImage: <T extends { file_name: string; preview_url: string }>(
    url: "v2/images",
    data: T
  ) => Promise<AxiosResponse>;

  putImage: <T extends FormData>(
    url: `images/${string}`,
    data: T,
    config: { headers: { "Content-Type": "multipart/form-data" } }
  ) => Promise<AxiosResponse>;

  deleteImage: (url: `v2/images/${string}`) => Promise<AxiosResponse>;

  getExperienceCommercialContent: <T extends ExperienceCommercialContent[]>(
    url: "experience-commercial-content",
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  getFlows: <T extends Flow[]>(url: "flows", config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;

  getCustomHighlights: <T extends GenericHighlight[]>(
    url: CustomHighlightsEndpoint,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  postCustomHighlight: <T extends Omit<GenericHighlight, "id">>(
    url: CustomHighlightsEndpoint,
    data: T,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  putCustomHighlight: <T extends Omit<GenericHighlight, "id">>(
    url: `${CustomHighlightsEndpoint}/${string}`,
    data: T,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  deleteCustomHighlight: <T extends GenericHighlight[]>(
    url: `${CustomHighlightsEndpoint}/${string}`,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  releaseTranslations: ReturnType<typeof releaseTranslations>;

  publishTranslation: ReturnType<typeof publishTranslation>;

  unpublishExperience: ReturnType<typeof unpublishExperience>;

  publishExperienceMedia: ReturnType<typeof publishExperienceMedia>;

  getExperienceOptionNames: ReturnType<typeof getExperienceOptionNames>;
  putExperienceOptionName: ReturnType<typeof putExperienceOptionName>;
  patchExperienceOptionName: ReturnType<typeof patchExperienceOptionName>;
  getExperienceOperationalTranslations: ReturnType<typeof getExperienceOperationalTranslations>;
  patchExperienceOperationalTranslation: ReturnType<typeof patchExperienceOperationalTranslation>;
};

function releaseTranslations(axiosInstance: AxiosInstance) {
  /**
   * Creates and releases translations for an experience.
   * Offer classification is resolved entirely in the backend; the frontend no longer
   * sends `own_offer_classification` (deprecated and removed from the request body).
   */
  return async (experienceId: string, languageList: string[]) => {
    return axiosInstance.post(`/experiences/${experienceId}/translations/release`, {
      language_list: languageList,
    });
  };
}

function publishTranslation(axiosInstance: AxiosInstance) {
  /**
   * Publishes the experience for the language on the query.
   *
   * @param force - if "force" is true, it wil remove "unpublish" status if exists.
   */
  return async (experienceId: string, languageCode: AvailableLanguage, force = false) => {
    return axiosInstance.post(`/experiences/${experienceId}/publish`, undefined, {
      params: {
        language_code: languageCode,
        experience_id: experienceId,
        force,
      },
    });
  };
}

function publishExperienceMedia(axiosInstance: AxiosInstance) {
  return async (experienceId: string) => {
    return axiosInstance.post(`/experiences/${experienceId}/publish-media`);
  };
}

function unpublishExperience(axiosInstance: AxiosInstance) {
  return async (experienceId: string, languageCode: AvailableLanguage) => {
    return axiosInstance.post(`/experiences/${experienceId}/unpublish`, undefined, {
      params: {
        language_code: languageCode,
        experience_id: experienceId,
      },
    });
  };
}

function getExperienceOptionNames(axiosInstance: AxiosInstance) {
  return async (experienceId: string) => {
    const filters = emit(builder.eq("experience_id", experienceId));
    return axiosInstance.get<Array<ExperienceOptionName>>(`/experience-option-names`, { params: { filters } });
  };
}

function putExperienceOptionName(axiosInstance: AxiosInstance) {
  return async (experienceOptionName: ExperienceOptionName) => {
    const { id, ...updatePayload } = experienceOptionName;
    return axiosInstance.put(`/experience-option-names/${id}`, updatePayload);
  };
}

type PatchExperienceOptionName = {
  id: string;
  premade_code?: string | null;
  translations?:
    | Array<{
        name: string;
        id?: string;
      }>
    | [];
};

function patchExperienceOptionName(axiosInstance: AxiosInstance) {
  return async (experienceOptionName: PatchExperienceOptionName) => {
    const { id, ...updatePayload } = experienceOptionName;
    return axiosInstance.patch(`/experience-option-names/${id}`, updatePayload);
  };
}

function getExperienceOperationalTranslations(axiosInstance: AxiosInstance) {
  return async (experienceId: string, languageCode?: string) => {
    const conditions = [builder.eq("experience_id", experienceId)];
    if (languageCode) {
      conditions.push(builder.eq("language_code", languageCode));
    }
    const filters = emit(conditions.length > 1 ? builder.and(...conditions) : conditions[0]);
    return axiosInstance.get<ExperienceOperationalTranslation[]>("/experience-operational-translations", {
      params: { filters },
    });
  };
}

function patchExperienceOperationalTranslation(axiosInstance: AxiosInstance) {
  return async (id: string, data: Partial<ExperienceOperationalTranslation>) => {
    return axiosInstance.patch(`/experience-operational-translations/${id}`, data);
  };
}
export function useContentCommandApi(): ContentCommandApi {
  const config = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: config.public.CONTENT_COMMAND_SERVICE_BASE_URL,
      headers: { "accept-version": "vnd.content-command-offer-service.v1" },
    },
    auth,
  });

  const axiosInstanceV2 = createHttpInstance({
    config: {
      baseURL: config.public.CONTENT_COMMAND_SERVICE_BASE_URL,
      headers: { "accept-version": "vnd.content-command-offer-service.v2" },
    },
    auth,
  });

  return {
    getTranslation: axiosInstance.get,
    getTranslations: axiosInstance.get,
    putTranslation: axiosInstanceV2.put,
    postTranslation: axiosInstance.post,
    putExperienceMedia: axiosInstance.put,
    postImage: axiosInstanceV2.post,
    putImage: axiosInstance.put,
    deleteImage: axiosInstanceV2.delete,
    getExperienceCommercialContent: axiosInstance.get,
    getFlows: axiosInstance.get,
    getCustomHighlights: axiosInstance.get,
    postCustomHighlight: axiosInstance.post,
    putCustomHighlight: axiosInstance.put,
    deleteCustomHighlight: axiosInstance.delete,
    releaseTranslations: releaseTranslations(axiosInstance),
    publishTranslation: publishTranslation(axiosInstance),
    unpublishExperience: unpublishExperience(axiosInstance),
    publishExperienceMedia: publishExperienceMedia(axiosInstance),
    getExperienceOptionNames: getExperienceOptionNames(axiosInstance),
    putExperienceOptionName: putExperienceOptionName(axiosInstance),
    patchExperienceOptionName: patchExperienceOptionName(axiosInstance),
    getExperienceOperationalTranslations: getExperienceOperationalTranslations(axiosInstance),
    patchExperienceOperationalTranslation: patchExperienceOperationalTranslation(axiosInstance),
    _axiosInstance: axiosInstance,
  };
}
